/**
 * Crop (cycle) day maths and phase for the pond page.
 *
 * DOC 1 = stocking day (#191), matching farm workbooks and the #155 import.
 * Prep day 1 = water-in day.
 */
import { differenceInCalendarDays, parseISO } from 'date-fns';
import type { ActiveCycleDetail, PondDetailResponse } from '@/types/generated/openapi';
import { todayInPH } from '@/lib/phDate';

export type CropPhase = 'prep' | 'grow_out' | 'closed';

export type Crop = ActiveCycleDetail;

export function cropPhase(crop: Crop): CropPhase {
  if (crop.phase) return crop.phase;
  if (crop.status === 'preparing') return 'prep';
  return crop.status === 'active' ? 'grow_out' : 'closed';
}

export function daysBetween(from: string, to: string): number {
  return differenceInCalendarDays(parseISO(to), parseISO(from));
}

/** DOC for a calendar date, or null before stocking / unstocked. */
export function docOn(crop: Crop, date: string): number | null {
  if (!crop.stocking_date) return null;
  const d = daysBetween(crop.stocking_date, date) + 1;
  return d >= 1 ? d : null;
}

/** Prep day for a calendar date before stocking, or null. */
export function prepDayOn(crop: Crop, date: string): number | null {
  if (!crop.prep_start_date) return null;
  if (crop.stocking_date && date >= crop.stocking_date) return null;
  const d = daysBetween(crop.prep_start_date, date) + 1;
  return d >= 1 ? d : null;
}

/** "DOC 34" / "Prep 5" / the date's own label when it falls outside the crop. */
export function cropDayLabel(crop: Crop, date: string): string | null {
  const doc = docOn(crop, date);
  if (doc !== null) return `DOC ${doc}`;
  const prep = prepDayOn(crop, date);
  return prep !== null ? `Prep ${prep}` : null;
}

/**
 * The day the crop page talks about as "today": today while open, else its
 * last day. Null for a closed crop with no recorded end (cancelled in prep,
 * or older closes), so its pages don't borrow the pond's later data.
 */
export function cropFocusDate(crop: Crop): string | null {
  if (cropPhase(crop) !== 'closed') return todayInPH();
  return crop.actual_harvest_date ?? null;
}

export function pondAreaM2(areaHa: string | number | null | undefined): number | null {
  const ha = areaHa === null || areaHa === undefined ? NaN : Number(areaHa);
  return Number.isFinite(ha) && ha > 0 ? ha * 10_000 : null;
}

export type PondFacts = Pick<PondDetailResponse, 'id' | 'name' | 'status' | 'area_ha' | 'pond_type' | 'alert_count'>;
